import {ScrollStrategy, ScrollStrategyOptions} from "@angular/cdk/overlay";
import {TextFieldModule} from "@angular/cdk/text-field";
import {AsyncPipe, DOCUMENT, DatePipe, NgClass, NgIf, NgTemplateOutlet} from "@angular/common";
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    signal,
    ViewChild,
    ViewEncapsulation,
} from "@angular/core";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {DavesaScrollbarDirective} from "@davesa/directives/scrollbar";
import {BehaviorSubject, Observable, Subject, take, takeUntil} from "rxjs";
import {DavesaDrawerComponent} from "@davesa/components/drawer";
import { SortByIndexPipe } from "@davesa/pipes/sortByIndex.pipe";
import { VisitsV2Service } from "app/modules/davesa/apps/visits/visits-v2.service";
import { ClinicNowV2Service } from "../clinic-now-list/clinic-now-v2.service";
import { ClinicNowListComponent } from "../clinic-now-list/clinic-now-list.component";

@Component({
    selector: "clinic-now",
    templateUrl: "./clinic-now.component.html",
    styleUrls: ["./clinic-now.component.scss"],
    encapsulation: ViewEncapsulation.None,
    exportAs: "clinicNow",
    imports: [
        NgClass,
        MatIconModule,
        MatButtonModule,
        DavesaScrollbarDirective,
        DavesaDrawerComponent,
        MatFormFieldModule,
        MatInputModule,
        TextFieldModule,
        NgIf,
        MatTooltipModule,
        NgClass,
        AsyncPipe,
        ClinicNowListComponent,
    ],
    providers: [SortByIndexPipe]
})
export class ClinicNowComponent implements OnInit, AfterViewInit, OnDestroy {
    private _visitsV2Service = inject(VisitsV2Service);
    private _clinicNowV2Service = inject(ClinicNowV2Service);
    @ViewChild("clinicInput") clinicInput: ElementRef;
    @Input() icon: boolean = false;
    @Input() iconSize: string = 'icon-size-6';
    @Input() iconTitle: string;
    @Input() oClinic: boolean = false;
    visit: any;
    visitInfo: any[];
    opened: boolean = false;
    selectedVisit: any;
    userRole: string;
    unfilterCount: number;
    subjectVisitOidFromUrl: string = "";

    visitsBoard = signal<any[] | null>(null);
    visitStatus = signal<any | null>(null);

    private _mutationObserver: MutationObserver;
    private _scrollStrategy: ScrollStrategy = this._scrollStrategyOptions.block();
    private _overlay: HTMLElement;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    public _clinicBoard: BehaviorSubject<any[] | null> = new BehaviorSubject(null);
    get clinicBoard$(): Observable<any[]> {
        return this._clinicBoard.asObservable();
    }

    @ViewChild(ClinicNowListComponent) private list!: ClinicNowListComponent;
    @ViewChild('clinicNowDrawer') clinicNowDrawer!: DavesaDrawerComponent;

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        private _scrollStrategyOptions: ScrollStrategyOptions,
        private sortByIndexPipe: SortByIndexPipe,

    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding("class") get classList(): any {
        return {
            "clinic-now-opened": this.opened,
        };
    }

    /**
     * Resize on 'input' and 'ngModelChange' events
     *
     * @private
     */
    @HostListener("input")
    @HostListener("ngModelChange")
    private _resizeMessageInput(): void {
        // This doesn't need to trigger Angular's change detection by itself
        this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                // Set the height to 'auto' so we can correctly read the scrollHeight
                this.clinicInput.nativeElement.style.height = "auto";

                // Get the scrollHeight and subtract the vertical padding
                this.clinicInput.nativeElement.style.height = `${this.clinicInput.nativeElement.scrollHeight}px`;
            });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit() {
        this._visitsV2Service.onInit().subscribe((response) => {
            this.visitStatus.set(this._visitsV2Service.visitStatus());
            this.sortByIndexPipe.transform(response, "asc");
        });
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        // Fix for Firefox.
        //
        // Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
        // adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
        // This fixes the problem by reading the 'top' value from the html element and adding it as a
        // 'marginTop' to the navigation itself.
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationTarget = mutation.target as HTMLElement;
                if (mutation.attributeName === "class") {
                    if (mutationTarget.classList.contains("cdk-global-scrollblock")) {
                        const top = parseInt(mutationTarget.style.top, 10);
                        this._renderer2.setStyle(this._elementRef.nativeElement, "margin-top", `${Math.abs(top)}px`);
                    } else {
                        this._renderer2.setStyle(this._elementRef.nativeElement, "margin-top", null);
                    }
                }
            });
        });
        this._mutationObserver.observe(this._document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });

        // Subscribe to drawer open event for refresh
        if (this.clinicNowDrawer?.openedChanged) {
            this.clinicNowDrawer.openedChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(async (open) => {
                if (open) {
                    this.list?.refresh();
                }
            });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Disconnect the mutation observer
        this._mutationObserver.disconnect();

        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Open the panel
     */
    async open(): Promise<void> {
        // Return if the panel has already opened
        if (this.opened) {
            return;
        }

        // Open the panel
        this._toggleOpened(true);
        this.list?.refresh();
    }

    /**
     * Close the panel
     */
    close(): void {
        // Return if the panel has already closed
        if (!this.opened) {
            return;
        }

        // Close the panel
        this._toggleOpened(false);
    }

    /**
     * Toggle the panel
     */
    toggle(): void {
        if (this.opened) {
            this.close();
        } else {
            this.open();
        }
    }

    /**
     * Select the participant
     *
     * @param id
     */
    // getByOid(id: string): void {
    //     console.log('selectParticipant', id)
    //     // Open the panel
    //     this._toggleOpened(true);

    //     // Get the participant data
    //     this._clinicNowService.getByOid(id).subscribe();
    // }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Show the backdrop
     *
     * @private
     */
    private _showOverlay(): void {
        // Try hiding the overlay in case there is one already opened
        this._hideOverlay();

        // Create the backdrop element
        this._overlay = this._renderer2.createElement("div");

        // Return if overlay couldn't be create for some reason
        if (!this._overlay) {
            return;
        }

        // Add a class to the backdrop element
        this._overlay.classList.add("quick-participant-overlay");

        // Append the backdrop to the parent of the panel
        this._renderer2.appendChild(this._elementRef.nativeElement.parentElement, this._overlay);

        // Enable block scroll strategy
        this._scrollStrategy.enable();

        // Add an event listener to the overlay
        this._overlay.addEventListener("click", () => {
            this.close();
        });
    }

    /**
     * Hide the backdrop
     *
     * @private
     */
    private _hideOverlay(): void {
        if (!this._overlay) {
            return;
        }

        // If the backdrop still exists...
        if (this._overlay) {
            // Remove the backdrop
            this._overlay.parentNode.removeChild(this._overlay);
            this._overlay = null;
        }

        // Disable block scroll strategy
        this._scrollStrategy.disable();
    }

    /**
     * Open/close the panel
     *
     * @param open
     * @private
     */
    private _toggleOpened(open: boolean): void {
        // Set the opened
        this.opened = open;

        // If the panel opens, show the overlay
        if (open) {
            this._showOverlay();
        }
        // Otherwise, hide the overlay
        else {
            this._hideOverlay();
        }
    }

    visitStatusClass(visit: any): any {
        // this._visitsStatus.pipe(take(1), map((visitStatuses: any) => {
        //     console.log('visitStatuses', visitStatuses)
        //     console.log('visitStatuses', visit.status)
        //     const visitStatus = visitStatuses.find((status: any) => status.status === visit.status);
        //     return visitStatus?.bgClass;
        // })).subscribe();
        return 'bg-emerald-200'
    }

    // async openVisit(item) {
    //     console.log('visit', item)
    //     this._davesaVisitService.setVisitInfo(item)

    // }

    selectVisit(visit: any): void {
        // Open the panel
        this._toggleOpened(true);

        // // Get the chat data
        // this._queryChatService.getChatById(id).subscribe();
    }

    async openVisit(visit) {
        // this._davesaVisitDrawerService.setVisit(visit)
    }
}
